/**
 * useChat hook - Re-export from @pdm/axel-ui-components
 *
 * This hook wraps the library's useChat and includes:
 * - Automatic Langfuse prompt management (fetches system prompt from Langfuse)
 * - Automatic Langfuse tracing (when environment variables are configured)
 * - LLM streaming via chat-lambda
 * - MCP tool calling (automatically configured via LLM client)
 * - Message management
 */

import { useMemo } from 'react';
import {
  useChat as useLibraryChat,
  createLLMClient,
  createLangfuseClient,
  UseChatProps as LibraryUseChatProps,
} from '@pdm/axel-ui-components';
import { useAuth } from '../contexts/AuthContext';

// Extend the library's UseChatProps but only use the fields we need
type UseChatProps = Pick<
  LibraryUseChatProps,
  'sessionID' | 'userId' | 'tags' | 'onMetadataGenerated'
>;

export function useChat({ sessionID, userId, tags, onMetadataGenerated }: UseChatProps) {
  const { apiClient, jwtToken, jwtReady } = useAuth();

  // Create LLM client with Cognito JWT
  // Authentication uses Cognito JWT exclusively - the backend validates via JWKS
  // and uses the same token for all services (chat-lambda, MCP, PDM tools)
  const llmClient = useMemo(() => {
    if (!jwtToken) {
      return undefined;
    }

    return createLLMClient({
      chatApiUrl: process.env.REACT_APP_AXEL_CHAT_API_URL,
      apiBaseUrl: process.env.REACT_APP_AXEL_API_URL,
      liteLLMApiKey: jwtToken,
      environment:
        (process.env.REACT_APP_ENVIRONMENT as 'dev' | 'staging' | 'prod' | 'production') || 'dev',
      langfusePublicKey: process.env.REACT_APP_AXEL_LANGFUSE_PUBLIC_KEY,
      langfuseSecretKey: process.env.REACT_APP_AXEL_LANGFUSE_SECRET_KEY,
      langfuseBaseUrl: process.env.REACT_APP_AXEL_LANGFUSE_BASE_URL,
      enableClientSideTracing: false,
    });
  }, [jwtToken]);

  // Initialize Langfuse client for prompt management
  const langfuseClient = useMemo(
    () =>
      createLangfuseClient({
        langfusePublicKey: process.env.REACT_APP_AXEL_LANGFUSE_PUBLIC_KEY,
        langfuseSecretKey: process.env.REACT_APP_AXEL_LANGFUSE_SECRET_KEY,
        langfuseBaseUrl: process.env.REACT_APP_AXEL_LANGFUSE_BASE_URL,
      }),
    []
  );

  return useLibraryChat({
    sessionID: jwtReady ? sessionID : null,
    apiClient,
    llmClient,
    langfuseClient,
    userId,
    tags,
    onMetadataGenerated,
  });
}
